import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PencilIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';

const stripHtml = (html: string | null): string => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

interface Owner {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface KnowledgeBase {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  ownerId: string | null;
  owner: Owner | null;
  createdAt: string;
  updatedAt: string;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const ACTIVE_FILTER_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const KnowledgeBaseList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can, loading: permissionsLoading } = useDynamicPermissions();

  const canView = can('knowledge_base', 'read');
  const canCreate = can('knowledge_base', 'create');
  const canUpdate = can('knowledge_base', 'update');
  const canDelete = can('knowledge_base', 'delete');

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 10;
  }, [searchParams]);

  const appliedSearch = searchParams.get('search') ?? '';
  const filterName = searchParams.get('name') ?? '';
  const filterDescription = searchParams.get('description') ?? '';
  const filterOwner = searchParams.get('ownerId') ?? '';
  const filterActive = searchParams.get('active') ?? '';

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed === appliedSearch) return;
      patchSearchParams({ search: trimmed || null, page: '1' });
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput, appliedSearch, patchSearchParams]);

  const { data: usersData } = useQuery({
    queryKey: ['users-kb-owner-filter'],
    queryFn: async () => {
      const response = await api.get('/users?limit=200&role=agent,manager,admin,super_admin');
      return response.data;
    },
    enabled: canView && !permissionsLoading,
  });

  const ownerOptions = useMemo(
    () =>
      (usersData?.users || []).map((u: Owner) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName}`.trim() || u.email,
      })),
    [usersData]
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['knowledge-bases', page, pageSize, appliedSearch, filterName, filterDescription, filterOwner, filterActive],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
      });
      if (appliedSearch) params.append('search', appliedSearch);
      if (filterName) params.append('name', filterName);
      if (filterDescription) params.append('description', filterDescription);
      if (filterOwner) params.append('ownerId', filterOwner);
      if (filterActive) params.append('active', filterActive);

      const response = await api.get(`/knowledge-base?${params}`);
      return response.data;
    },
    enabled: canView && !permissionsLoading,
  });

  const knowledgeBases: KnowledgeBase[] = data?.knowledgeBases || [];
  const total = data?.pagination?.total || 0;
  const totalPages = Math.ceil(total / pageSize) || 1;

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'name':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterName}
              placeholder="Filter by title..."
              onChange={(v) => {
                patchSearchParams({ name: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'description':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterDescription}
              placeholder="Filter by description..."
              onChange={(v) => {
                patchSearchParams({ description: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'owner':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={ownerOptions}
              value={filterOwner}
              placeholder="All Owners"
              onChange={(v) => {
                patchSearchParams({ ownerId: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={ACTIVE_FILTER_OPTIONS}
              value={filterActive}
              placeholder="All Statuses"
              onChange={(v) => {
                patchSearchParams({ active: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [filterName, filterDescription, filterOwner, filterActive, ownerOptions, patchSearchParams]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedSearch) {
      chips.push({ id: 'search', label: 'Search', value: appliedSearch, color: 'gray' });
    }
    if (filterName) {
      chips.push({ id: 'name', label: 'Title', value: filterName, color: 'blue' });
    }
    if (filterDescription) {
      chips.push({ id: 'description', label: 'Description', value: filterDescription, color: 'blue' });
    }
    if (filterOwner) {
      const owner = ownerOptions.find((o: { value: string; label: string }) => o.value === filterOwner);
      chips.push({ id: 'ownerId', label: 'Owner', value: owner?.label || 'Unknown', color: 'purple' });
    }
    if (filterActive) {
      const label = ACTIVE_FILTER_OPTIONS.find((o) => o.value === filterActive)?.label || filterActive;
      chips.push({ id: 'active', label: 'Status', value: label, color: 'green' });
    }
    return chips;
  }, [appliedSearch, filterName, filterDescription, filterOwner, filterActive, ownerOptions]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'name':
        patchSearchParams({ name: null, page: '1' });
        break;
      case 'description':
        patchSearchParams({ description: null, page: '1' });
        break;
      case 'ownerId':
        patchSearchParams({ ownerId: null, page: '1' });
        break;
      case 'active':
        patchSearchParams({ active: null, page: '1' });
        break;
    }
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      name: null,
      description: null,
      ownerId: null,
      active: null,
      page: '1',
    });
  };

  const handleDelete = async (id: string, name: string) => {
    confirmAlert({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      buttons: [
        {
          label: 'Cancel',
          onClick: () => {},
          className: 'px-6 py-2.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50',
        },
        {
          label: 'Delete',
          onClick: async () => {
            try {
              await api.delete(`/knowledge-base/${id}`);
              toast.success('Knowledge base deleted successfully');
              refetch();
            } catch (err: unknown) {
              const message =
                err && typeof err === 'object' && 'response' in err
                  ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
                  : undefined;
              toast.error(message || 'Failed to delete knowledge base');
            }
          },
          className: 'px-6 py-2.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700',
        },
      ],
    });
  };

  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">You don't have permission to view knowledge bases.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Knowledge Base</h1>
              <p className="text-xs text-gray-500">Manage knowledge bases for organizing articles</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or description..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-8 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              {canCreate && (
                <Link
                  to="/knowledge-base/new"
                  className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md shadow-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                  New
                </Link>
              )}
            </div>
          </div>

          {activeFilterChips.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <ActiveFilterChips
                chips={activeFilterChips}
                onRemove={handleRemoveFilterChip}
                onClearAll={handleClearAllFilters}
              />
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-2 bg-red-50 border border-red-200 rounded-lg p-4 flex-shrink-0">
          <p className="text-red-800">Failed to fetch knowledge bases. Please try again.</p>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : knowledgeBases.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No knowledge bases found.</p>
          </div>
        ) : (
          <div className="overflow-auto flex-1">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('name', 'Title')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('description', 'Description')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('owner', 'Owner')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('status', 'Status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {knowledgeBases.map((kb) => (
                  <tr key={kb.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{kb.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {stripHtml(kb.description) || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {kb.owner ? `${kb.owner.firstName} ${kb.owner.lastName}` : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          kb.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {kb.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {canUpdate && (
                          <Link
                            to={`/knowledge-base/${kb.id}/edit`}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Edit"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </Link>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(kb.id, kb.name)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && total > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-white flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Show</span>
              <select
                value={pageSize}
                onChange={(e) => patchSearchParams({ pageSize: e.target.value, page: '1' })}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-700">entries</span>
            </div>

            <div className="text-sm text-gray-700">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} entries
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => patchSearchParams({ page: String(Math.max(1, page - 1)) })}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => patchSearchParams({ page: String(Math.min(totalPages, page + 1)) })}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBaseList;
